import { Button, SectionSettingsWrapper } from '@acme/ui';
import { useAtomValue, useSetAtom } from 'jotai';
import React, { useMemo } from 'react';
import {
  apiKeyAtom,
  apiKeyAnthropicAtom,
  apiKeyGoogleAtom,
  apiKeyXaiAtom,
  apiKeyDeepseekAtom,
  apiKeyGlmAtom,
  apiKeyKimiAtom,
  apiKeyQwenAtom,
  type ApiKeyProvider,
} from '../../store/setting';
import { apiKeyModalAtom } from '../../store/user';

// Provider configuration with enhanced styling
const PROVIDERS: {
  id: ApiKeyProvider;
  name: string;
  description: string;
  url: string;
  placeholder: string;
  features: string[];
}[] = [
    {
      id: 'openai',
      name: 'OpenAI',
      description: 'GPT-4o, GPT-5, o1, o3 and more',
      url: 'https://platform.openai.com/api-keys',
      placeholder: 'sk-...',
      features: ['GPT-5.2', 'o3 Reasoning', 'DALL-E 3', 'Vision'],
    },
    {
      id: 'anthropic',
      name: 'Anthropic',
      description: 'Claude 3.5, Claude 4 Opus and more',
      url: 'https://console.anthropic.com/settings/keys',
      placeholder: 'sk-ant-...',
      features: ['Claude 4 Opus', '200K Context', 'Artifacts', 'Code'],
    },
    {
      id: 'google',
      name: 'Google AI',
      description: 'Gemini 2.0, Gemini Pro and more',
      url: 'https://aistudio.google.com/apikey',
      placeholder: 'AIza...',
      features: ['Gemini 2.0', '2M Context', 'Flash Speed', 'Multimodal'],
    },
    {
      id: 'xai',
      name: 'xAI (Grok)',
      description: 'Grok 2, Grok 3 and more',
      url: 'https://console.x.ai/',
      placeholder: 'xai-...',
      features: ['Grok 3', 'Real-time', 'Uncensored', 'X Data'],
    },
    {
      id: 'deepseek',
      name: 'DeepSeek',
      description: 'DeepSeek V3, R1 and more',
      url: 'https://platform.deepseek.com/api_keys',
      placeholder: 'sk-...',
      features: ['DeepSeek R1', 'Reasoning', 'Low Cost', 'Open'],
    },
    {
      id: 'glm',
      name: 'Z.AI (GLM)',
      description: 'GLM-5.2, GLM-5 and GLM-4.7',
      url: 'https://z.ai/manage-apikey/apikey-list',
      placeholder: '...',
      features: ['GLM-5.2', '1M Context', 'Thinking', 'Coding'],
    },
    {
      id: 'kimi',
      name: 'Moonshot (Kimi)',
      description: 'Kimi K3 and K2.7 Code',
      url: 'https://platform.moonshot.ai/console/api-keys',
      placeholder: 'sk-...',
      features: ['Kimi K3', '1M Context', 'Vision', 'Reasoning'],
    },
    {
      id: 'qwen',
      name: 'Alibaba (Qwen)',
      description: 'Qwen3.7 Max, Plus and Flash',
      url: 'https://modelstudio.console.alibabacloud.com/',
      placeholder: 'sk-...',
      features: ['Qwen3.7 Max', 'Long Context', 'Multimodal', 'Low Cost'],
    },
  ];

// Provider icons
const ProviderIcon: React.FC<{ provider: ApiKeyProvider; className?: string }> = ({ provider, className = '' }) => {
  const icons: Record<ApiKeyProvider, React.ReactNode> = {
    openai: (
      <svg className={className} viewBox="0 0 24 24" fill="currentColor">
        <path d="M22.282 9.821a5.985 5.985 0 0 0-.516-4.91 6.046 6.046 0 0 0-6.51-2.9A6.065 6.065 0 0 0 4.981 4.18a5.985 5.985 0 0 0-3.998 2.9 6.046 6.046 0 0 0 .743 7.097 5.98 5.98 0 0 0 .51 4.911 6.051 6.051 0 0 0 6.515 2.9A5.985 5.985 0 0 0 13.26 24a6.056 6.056 0 0 0 5.772-4.206 5.99 5.99 0 0 0 3.997-2.9 6.056 6.056 0 0 0-.747-7.073zM13.26 22.43a4.476 4.476 0 0 1-2.876-1.04l.141-.081 4.779-2.758a.795.795 0 0 0 .392-.681v-6.737l2.02 1.168a.071.071 0 0 1 .038.052v5.583a4.504 4.504 0 0 1-4.494 4.494zM3.6 18.304a4.47 4.47 0 0 1-.535-3.014l.142.085 4.783 2.759a.771.771 0 0 0 .78 0l5.843-3.369v2.332a.08.08 0 0 1-.033.062L9.74 19.95a4.5 4.5 0 0 1-6.14-1.646zM2.34 7.896a4.485 4.485 0 0 1 2.366-1.973V11.6a.766.766 0 0 0 .388.676l5.815 3.355-2.02 1.168a.076.076 0 0 1-.071 0l-4.83-2.786A4.504 4.504 0 0 1 2.34 7.872zm16.597 3.855l-5.833-3.387L15.119 7.2a.076.076 0 0 1 .071 0l4.83 2.791a4.494 4.494 0 0 1-.676 8.105v-5.678a.79.79 0 0 0-.407-.667zm2.01-3.023l-.141-.085-4.774-2.782a.776.776 0 0 0-.785 0L9.409 9.23V6.897a.066.066 0 0 1 .028-.061l4.83-2.787a4.5 4.5 0 0 1 6.68 4.66zm-12.64 4.135l-2.02-1.164a.08.08 0 0 1-.038-.057V6.075a4.5 4.5 0 0 1 7.375-3.453l-.142.08L8.704 5.46a.795.795 0 0 0-.393.681zm1.097-2.365l2.602-1.5 2.607 1.5v2.999l-2.597 1.5-2.607-1.5z" />
      </svg>
    ),
    anthropic: (
      <svg className={className} viewBox="0 0 24 24" fill="currentColor">
        <path d="M17.304 3.541h-3.672l6.696 16.918h3.672l-6.696-16.918zm-10.608 0L0 20.459h3.744l1.368-3.6h6.624l1.368 3.6h3.744L10.152 3.541H6.696zm.456 10.8l2.304-6.072 2.304 6.072H7.152z" />
      </svg>
    ),
    google: (
      <svg className={className} viewBox="0 0 24 24" fill="currentColor">
        <path d="M12.48 10.92v3.28h7.84c-.24 1.84-.853 3.187-1.787 4.133-1.147 1.147-2.933 2.4-6.053 2.4-4.827 0-8.6-3.893-8.6-8.72s3.773-8.72 8.6-8.72c2.6 0 4.507 1.027 5.907 2.347l2.307-2.307C18.747 1.44 16.133 0 12.48 0 5.867 0 .307 5.387.307 12s5.56 12 12.173 12c3.573 0 6.267-1.173 8.373-3.36 2.16-2.16 2.84-5.213 2.84-7.667 0-.76-.053-1.467-.173-2.053H12.48z" />
      </svg>
    ),
    xai: (
      <svg className={className} viewBox="0 0 24 24" fill="currentColor">
        <path d="M18.901 1.153h3.68l-8.04 9.19L24 22.846h-7.406l-5.8-7.584-6.638 7.584H.474l8.6-9.83L0 1.154h7.594l5.243 6.932ZM17.61 20.644h2.039L6.486 3.24H4.298Z" />
      </svg>
    ),
    deepseek: (
      <svg className={className} viewBox="0 0 24 24" fill="currentColor">
        <circle cx="12" cy="12" r="10" strokeWidth="2" stroke="currentColor" fill="none" />
        <path d="M12 6v12M6 12h12" strokeWidth="2" stroke="currentColor" strokeLinecap="round" />
      </svg>
    ),
    glm: (
      <svg className={className} viewBox="0 0 24 24" fill="none">
        <path d="M12 2.5 21 7v10l-9 4.5L3 17V7l9-4.5Z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
        <path d="M12 8l4.5 2.4v4.8L12 17.6 7.5 15.2v-4.8L12 8Z" fill="currentColor" />
      </svg>
    ),
    kimi: (
      <svg className={className} viewBox="0 0 24 24" fill="currentColor">
        <path d="M20.5 15.6A8.4 8.4 0 0 1 9 4a9 9 0 1 0 11.5 11.6Z" />
      </svg>
    ),
    qwen: (
      <svg className={className} viewBox="0 0 24 24" fill="none">
        <path d="M4.5 7.8 12 3.5l7.5 4.3v8.4L12 20.5l-7.5-4.3V7.8Z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
        <path d="M12 9v6.8M8.4 10.8v3.2M15.6 10.8v3.2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      </svg>
    ),
  };
  return <>{icons[provider]}</>;
};

// Helper to mask API key
const maskApiKey = (key: string | undefined): string => {
  if (!key || key.length < 8) return key || '';
  return key.slice(0, 4) + '•'.repeat(Math.min(key.length - 8, 16)) + key.slice(-4);
};

interface ApiKeyCardProps {
  provider: (typeof PROVIDERS)[number];
  apiKey: string | undefined;
  onUpdate: () => void;
}

const ApiKeyCard: React.FC<ApiKeyCardProps> = ({ provider, apiKey, onUpdate }) => {
  const hasKey = !!apiKey;

  return (
    <div
      className={`group relative rounded-2xl border transition-colors overflow-hidden ${hasKey
        ? 'border-gray-200 dark:border-neutral-800 hover:border-gray-300 dark:hover:border-neutral-700'
        : 'border-dashed border-gray-300 dark:border-neutral-700 hover:border-gray-400 dark:hover:border-neutral-600'
        }`}
    >
      {/* Content */}
      <div className="relative p-5 bg-white dark:bg-neutral-900">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-4 flex-1 min-w-0">
            {/* Icon */}
            <div className="w-12 h-12 rounded-xl bg-gray-900 dark:bg-neutral-800 flex items-center justify-center shadow-sm shrink-0 border border-gray-200 dark:border-neutral-700">
              <ProviderIcon provider={provider.id} className="w-6 h-6 text-white/90" />
            </div>

            {/* Info */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="font-semibold text-base text-gray-900 dark:text-neutral-100">{provider.name}</h3>
                {hasKey ? (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-300 border border-emerald-200/60 dark:border-emerald-800/40">
                    <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    Connected
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-50 text-amber-800 dark:bg-amber-900/20 dark:text-amber-300 border border-amber-200/60 dark:border-amber-800/40">
                    <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                    Setup Required
                  </span>
                )}
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{provider.description}</p>

              {/* Features pills */}
              <div className="flex flex-wrap gap-1.5 mt-2">
                {provider.features.map((feature, idx) => (
                  <span
                    key={idx}
                    className="px-2 py-0.5 text-xs font-medium rounded-md bg-gray-50 dark:bg-neutral-800 text-gray-600 dark:text-neutral-300 border border-gray-200 dark:border-neutral-700"
                  >
                    {feature}
                  </span>
                ))}
              </div>

              {/* API Key display */}
              {hasKey && (
                <div className="mt-3">
                  <code className="inline-block px-3 py-1.5 text-xs font-mono bg-gray-50 dark:bg-neutral-800 rounded-lg border border-gray-200 dark:border-neutral-700 text-gray-700 dark:text-neutral-200">
                    {maskApiKey(apiKey)}
                  </code>
                </div>
              )}
            </div>
          </div>

          {/* Action button */}
          <Button
            className={`shrink-0 px-4 py-2 text-sm font-semibold rounded-xl transition-colors ${hasKey
              ? 'bg-white hover:bg-gray-50 dark:bg-neutral-900 dark:hover:bg-neutral-800 text-gray-700 dark:text-neutral-200 border border-gray-200 dark:border-neutral-700'
              : 'bg-blue-600 hover:bg-blue-700 text-white'
              }`}
            onClick={onUpdate}
          >
            {hasKey ? 'Edit' : 'Add Key'}
          </Button>
        </div>

        {/* Urgency callout for missing key */}
        {!hasKey && (
          <div className="mt-4 p-3 rounded-xl bg-amber-50 dark:bg-amber-900/10 border border-amber-200/60 dark:border-amber-800/40">
            <div className="flex items-center gap-2">
              <svg className="w-5 h-5 text-amber-500 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
              <div className="flex-1">
                <p className="text-sm font-semibold text-amber-800 dark:text-amber-200">
                  Unlock {provider.name} models now
                </p>
                <p className="text-xs text-amber-700/80 dark:text-amber-300/60 mt-0.5">
                  <a
                    href={provider.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline hover:no-underline"
                  >
                    Get your free API key
                  </a>
                  {' '}to start using these powerful models
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

interface AccountProps { }

export const Account: React.FC<AccountProps> = () => {
  const savedApiKeyOpenAI = useAtomValue(apiKeyAtom);
  const savedApiKeyAnthropic = useAtomValue(apiKeyAnthropicAtom);
  const savedApiKeyGoogle = useAtomValue(apiKeyGoogleAtom);
  const savedApiKeyXai = useAtomValue(apiKeyXaiAtom);
  const savedApiKeyDeepseek = useAtomValue(apiKeyDeepseekAtom);
  const savedApiKeyGlm = useAtomValue(apiKeyGlmAtom);
  const savedApiKeyKimi = useAtomValue(apiKeyKimiAtom);
  const savedApiKeyQwen = useAtomValue(apiKeyQwenAtom);
  const setApiKeyModal = useSetAtom(apiKeyModalAtom);

  const apiKeys = useMemo(
    () => ({
      openai: savedApiKeyOpenAI?.key,
      anthropic: savedApiKeyAnthropic?.key,
      google: savedApiKeyGoogle?.key,
      xai: savedApiKeyXai?.key,
      deepseek: savedApiKeyDeepseek?.key,
      glm: savedApiKeyGlm?.key,
      kimi: savedApiKeyKimi?.key,
      qwen: savedApiKeyQwen?.key,
    }),
    [savedApiKeyOpenAI, savedApiKeyAnthropic, savedApiKeyGoogle, savedApiKeyXai, savedApiKeyDeepseek, savedApiKeyGlm, savedApiKeyKimi, savedApiKeyQwen]
  );

  const configuredCount = Object.values(apiKeys).filter(Boolean).length;
  const progress = (configuredCount / PROVIDERS.length) * 100;

  return (
    <div className="space-y-6">
      <SectionSettingsWrapper title="API Keys">
        {/* Header with progress */}
        <div className="mb-6 p-4 rounded-2xl bg-gray-50 dark:bg-neutral-900 border border-gray-200 dark:border-neutral-800">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h3 className="font-semibold text-gray-900 dark:text-gray-100">AI Provider Setup</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                Connect your API keys to unlock AI models. Keys are stored securely in your browser.
              </p>
            </div>
            <div className="text-right">
              <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                {configuredCount}<span className="text-gray-400 dark:text-gray-500">/{PROVIDERS.length}</span>
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400">configured</div>
            </div>
          </div>

          {/* Progress bar */}
          <div className="relative h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
            <div
              className={`absolute inset-y-0 left-0 rounded-full transition-all duration-500 ${progress === 100
                ? 'bg-emerald-500'
                : progress >= 60
                  ? 'bg-blue-500'
                  : 'bg-amber-500'
                }`}
              style={{ width: `${progress}%` }}
            />
          </div>

          {/* Status message */}
          {configuredCount === 0 && (
            <div className="mt-3 flex items-center gap-2 text-amber-600 dark:text-amber-400">
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <span className="text-sm font-medium">Add at least one API key to start chatting</span>
            </div>
          )}
          {configuredCount > 0 && configuredCount < PROVIDERS.length && (
            <div className="mt-3 flex items-center gap-2 text-blue-600 dark:text-blue-400">
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
              <span className="text-sm font-medium">Add more providers to unlock all models</span>
            </div>
          )}
          {configuredCount === PROVIDERS.length && (
            <div className="mt-3 flex items-center gap-2 text-green-600 dark:text-green-400">
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <span className="text-sm font-medium">All providers configured! You have access to all models.</span>
            </div>
          )}
        </div>

        {/* Provider cards */}
        <div className="grid gap-4">
          {PROVIDERS.map((provider) => (
            <ApiKeyCard
              key={provider.id}
              provider={provider}
              apiKey={apiKeys[provider.id]}
              onUpdate={() => setApiKeyModal(provider.id)}
            />
          ))}
        </div>
      </SectionSettingsWrapper>
    </div>
  );
};